/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { isExternalLink } from '../../../Utilities/isExternalLink';

export interface ILogoProps {
    image: Msdyn365.IImageData;
    gridSettings?: Msdyn365.IGridSettings;
    link?: ILinkData;
    className?: string;
    telemetryContent?: ITelemetryContent;
    requestContext: Msdyn365.IRequestContext;
}

export interface ILinkData {
    linkUrl: Msdyn365.ILinkData;
    ariaLabel?: string;
    openInNewTab?: boolean;
    linkText?: string;
}

/**
 *
 * Logo component
 * @extends {React.PureComponent<ILogoConfig>}
 */
export class Logo extends React.PureComponent<ILogoProps> {
    private attributes?: Msdyn365.IDictionary<string>;

    constructor(props: ILogoProps) {
        super(props);
        const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.Logo);
        this.attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    }
    public render(): JSX.Element {
        return (
            // tslint:disable-next-line: react-a11y-event-has-role
            <div
                className={this.props.className}
                // tslint:disable-next-line: jsx-no-lambda react-this-binding-issue
                onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    // @ts-ignore
                    if (utag) {
                        event.preventDefault();
                        const isExternal = isExternalLink(this.props.link?.linkUrl.destinationUrl);
                        const eventName = isExternal ? 'outbound-general-link-click' : 'internal-navigation';
                        const eventCategory = isExternal ? 'outbound links' : 'internal links';
                        const internalDestinationUrl =
                            this.props.link?.linkUrl.destinationUrl === '/'
                                ? '/'
                                : this.props.link?.linkUrl.destinationUrl.split(location.host)[1];
                        const internalLocalUrl =
                            Msdyn365.msdyn365Commerce.isBrowser &&
                            (window.location.href === '/' ? '/' : window.location.href.split(location.host)[1].split('?')[0]);
                        const eventAction = isExternal ? `to: ${this.props.link?.linkUrl.destinationUrl}` : `to: ${internalDestinationUrl}`;
                        const eventLabel = isExternal
                            ? `from ${this.props.link?.ariaLabel} logo: ${Msdyn365.msdyn365Commerce.isBrowser && window.location.href}`
                            : `from ${this.props.link?.ariaLabel} logo: ${internalLocalUrl}`;
                        // @ts-ignore
                        // tslint:disable-next-line: object-literal-key-quotes
                        utag.link({
                            event: eventName,
                            event_category: eventCategory,
                            event_action: eventAction,
                            event_label: eventLabel,
                            event_noninteraction: 'false'
                        });
                        if (Msdyn365.msdyn365Commerce.isBrowser) {
                            const isOpenInNewTab = this.props.link?.openInNewTab;
                            !isOpenInNewTab
                                ? (window.location.href = this.props.link?.linkUrl.destinationUrl!)
                                : window.open(this.props.link?.linkUrl.destinationUrl);
                        }
                    }
                }}
            >
                {this._renderLogo(this.props)}
            </div>
        );
    }

    private _renderLogo(config: ILogoProps): JSX.Element {
        if (config.link && config.link.linkUrl.destinationUrl) {
            return (
                <a
                    href={config.link.linkUrl.destinationUrl}
                    aria-label={config.link.ariaLabel}
                    target={config.link.openInNewTab ? '_blank' : undefined}
                    {...this.attributes}
                >
                    {this._renderImage(config)}
                </a>
            );
        }
        return this._renderImage(config);
    }

    private _renderImage(config: ILogoProps): JSX.Element {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: `w=0&h=0&m=6`, w: 0, h: 0 },
                lg: { q: `w=0&h=0&m=6`, w: 0, h: 0 },
                xl: { q: `w=0&h=0&m=6`, w: 0, h: 0 }
            },
            lazyload: false,
            disableLazyLoad: true
        };
        // TODO: add imageSettings to logo from config
        // config && config.image && config.image.imageSettings || defaultImageSettings
        return (
            <Msdyn365.Image
                {...config.image}
                gridSettings={this.props.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='default'
                editProps={{ key: config.image || {}, requestContext: this.props.requestContext }}
            />
        );
    }
}

export default Logo;
